import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Grid,
  Alert,
  Snackbar,
  Chip,
  Card,
  CardContent,
  Divider,
  IconButton,
  Collapse,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon,
  GetApp as GetAppIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Footer from '../components/Footer';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

function ClientsJournee() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [expandedClients, setExpandedClients] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalClients, setTotalClients] = useState(0);
  const [totalFactures, setTotalFactures] = useState(0);
  const [filterStatut, setFilterStatut] = useState('tous');
  const [filteredClients, setFilteredClients] = useState([]);
  const navigate = useNavigate();

  const statuts = [
    { value: 'tous', label: 'Tous les statuts' },
    { value: 'A payer', label: 'A payer', color: 'warning' },
    { value: 'Payé', label: 'Payé', color: 'success' },
    { value: 'A envoyer', label: 'A envoyer', color: 'info' },
    { value: 'Envoyée', label: 'Envoyée', color: 'primary' },
  ];

  const fetchClientsJournee = useCallback(async (date) => {
    setLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await axios.get(`/api/clients/journee?date=${dateStr}`);
      setClients(response.data.clients || []);
      setTotalClients(response.data.total_clients || 0);
      setTotalFactures(response.data.total_factures || 0);
      setFilteredClients(response.data.clients || []);
    } catch (error) {
      console.error('Erreur lors du chargement des clients de la journée:', error);
      showSnackbar('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClientsJournee(selectedDate);
  }, [selectedDate, fetchClientsJournee]);

  useEffect(() => {
    // Filtrer les clients selon le statut sélectionné
    if (filterStatut === 'tous') {
      setFilteredClients(clients);
    } else {
      const filtered = clients.filter(client => 
        client.factures.some(facture => facture.facture_statut === filterStatut)
      );
      setFilteredClients(filtered);
    }
    setPage(0); // Reset à la première page lors du filtrage
  }, [clients, filterStatut]);

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  const handleRefresh = () => {
    fetchClientsJournee(selectedDate);
  };

  const handleExpandClient = (clientId) => {
    setExpandedClients(prev => ({
      ...prev,
      [clientId]: !prev[clientId]
    }));
  };

  const handleViewFacture = (factureId) => {
    navigate(`/factures/${factureId}`);
  };

  const handleDownloadPDF = async (factureId) => {
    try {
      const response = await axios.get(`/api/factures/${factureId}/pdf`, {
        responseType: 'blob',
      });
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `facture-${factureId}.pdf`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      showSnackbar('Erreur lors du téléchargement du PDF', 'error');
    }
  };

  const handleDownloadDailyInvoice = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(`/api/factures/journaliere/pdf?date=${dateStr}`, {
        responseType: 'blob',
      });
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `facture_journaliere_${dateStr}.pdf`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showSnackbar('Facture journalière téléchargée avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors du téléchargement de la facture journalière:', error);
      showSnackbar('Erreur lors du téléchargement de la facture journalière', 'error');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    const statutObj = statuts.find(s => s.value === status);
    return statutObj ? statutObj.color : 'default';
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Calculer les totaux
  const totalMontantTTC = clients.reduce((total, client) => {
    return total + client.factures.reduce((clientTotal, facture) => {
      return clientTotal + (facture.total_ttc || 0);
    }, 0);
  }, 0);

  const totalMontantHT = clients.reduce((total, client) => {
    return total + client.factures.reduce((clientTotal, facture) => {
      return clientTotal + (facture.total_ht || 0);
    }, 0);
  }, 0);

  const totalTVA = totalMontantTTC - totalMontantHT;

  // Pagination
  const paginatedClients = filteredClients.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Chargement des clients de la journée...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pb: 8 }}>
      {/* En-tête avec sélection de date et filtres */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h4">Clients de la journée</Typography>
          <Box display="flex" gap={2} alignItems="center">
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handleDownloadDailyInvoice}
              color="primary"
              disabled={clients.length === 0}
            >
              Facture Journalière
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
            >
              Actualiser
            </Button>
            <Button
              variant="outlined"
              startIcon={<AssessmentIcon />}
              onClick={() => navigate('/factures')}
            >
              Voir toutes les factures
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
              <DatePicker
                label="Date"
                value={selectedDate}
                onChange={handleDateChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filtrer par statut</InputLabel>
              <Select
                value={filterStatut}
                label="Filtrer par statut"
                onChange={(e) => setFilterStatut(e.target.value)}
              >
                {statuts.map((statut) => (
                  <MenuItem key={statut.value} value={statut.value}>
                    {statut.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Statistiques */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Nombre de clients
              </Typography>
              <Typography variant="h4">
                {totalClients}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Nombre de factures
              </Typography>
              <Typography variant="h4">
                {totalFactures}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total TTC
              </Typography>
              <Typography variant="h4" color="primary">
                {totalMontantTTC.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Liste des clients */}
      {paginatedClients.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          sx={{ py: 8 }}
        >
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Aucun client trouvé pour cette journée
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {format(selectedDate, 'dd/MM/yyyy', { locale: fr })}
          </Typography>
        </Box>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden', flexGrow: 1 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Factures</TableCell>
                  <TableCell>Total TTC</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedClients.map((client) => (
                  <React.Fragment key={client.client_id}>
                    <TableRow hover>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {client.client_prenom ? `${client.client_prenom} ${client.client_nom}` : client.client_nom}
                        </Typography>
                        {client.client_adresse && (
                          <Typography variant="body2" color="textSecondary">
                            {client.client_adresse}
                            {client.client_code_postal && client.client_ville && 
                              `, ${client.client_code_postal} ${client.client_ville}`
                            }
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {client.client_email || '-'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {client.client_telephone || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {client.factures.length} facture{client.factures.length > 1 ? 's' : ''}
                        </Typography>
                        {client.factures.length > 0 && (
                          <IconButton
                            size="small"
                            onClick={() => handleExpandClient(client.client_id)}
                          >
                            {expandedClients[client.client_id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {client.factures.reduce((total, facture) => total + (facture.total_ttc || 0), 0)
                            .toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleExpandClient(client.client_id)}
                          title="Voir les détails"
                        >
                          {expandedClients[client.client_id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    
                    {/* Détails des factures */}
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 0 }}>
                        <Collapse in={expandedClients[client.client_id]} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              Détails des factures
                            </Typography>
                            {client.factures.map((facture) => (
                              <Card key={facture.facture_id} sx={{ mb: 2 }}>
                                <CardContent>
                                  <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={3}>
                                      <Typography variant="body2" color="textSecondary">
                                        Facture
                                      </Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                                        {facture.facture_numero}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                      <Typography variant="body2" color="textSecondary">
                                        Statut
                                      </Typography>
                                      <Chip
                                        label={facture.facture_statut}
                                        color={getStatusColor(facture.facture_statut)}
                                        size="small"
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                      <Typography variant="body2" color="textSecondary">
                                        Montant TTC
                                      </Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {facture.total_ttc.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                      <Typography variant="body2" color="textSecondary">
                                        Services
                                      </Typography>
                                      <Box>
                                        {facture.lignes && facture.lignes.length > 0 ? (
                                          facture.lignes.map((ligne, index) => (
                                            <Typography key={index} variant="body2" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                                              {ligne.quantite}x {ligne.article_nom}
                                            </Typography>
                                          ))
                                        ) : (
                                          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                            Aucun détail
                                          </Typography>
                                        )}
                                      </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                      <Box display="flex" gap={1}>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleViewFacture(facture.facture_id)}
                                          title="Voir la facture"
                                        >
                                          <VisibilityIcon />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleDownloadPDF(facture.facture_id)}
                                          title="Télécharger PDF"
                                        >
                                          <GetAppIcon />
                                        </IconButton>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                  {facture.facture_notes && (
                                    <>
                                      <Divider sx={{ my: 1 }} />
                                      <Typography variant="body2" color="textSecondary">
                                        <strong>Notes:</strong> {facture.facture_notes}
                                      </Typography>
                                    </>
                                  )}
                                </CardContent>
                              </Card>
                            ))}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredClients.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Lignes par page:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
            }
          />
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <Footer />
    </Box>
  );
}

export default ClientsJournee;
